import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  api,
  setCsrfToken,
  type PortalAuthMeResponse,
  type PortalPublicConfig,
  type PortalUserProfile,
} from '../services/api';

interface AuthContextValue {
  user: PortalUserProfile | null;
  loading: boolean;
  config: PortalPublicConfig | null;
  login: (payload: Record<string, unknown>) => Promise<boolean>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function applyAuth(data: PortalAuthMeResponse | undefined): PortalUserProfile | null {
  if (data?.authenticated && data.user) {
    setCsrfToken(data.csrf_token ?? null);
    return data.user;
  }
  setCsrfToken(null);
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUserProfile | null>(null);
  const [config, setConfig] = useState<PortalPublicConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const res = await api.getMe();
    setUser(applyAuth(res.data));
  }, []);

  useEffect(() => {
    (async () => {
      const [cfg, me] = await Promise.all([api.getConfig(), api.getMe()]);
      if (cfg.data) setConfig(cfg.data);
      setUser(applyAuth(me.data));
      setLoading(false);
    })();
  }, []);

  const login = useCallback(async (payload: Record<string, unknown>) => {
    const res = await api.loginTelegram(payload);
    if (res.success && res.data?.authenticated) {
      setUser(applyAuth(res.data));
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
    setCsrfToken(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, config, login, logout, refresh }),
    [user, loading, config, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
